/**
 * Cast — Client-side video merge engine
 * Uses ffmpeg.wasm (single-threaded core, no SharedArrayBuffer required)
 * to concatenate Veo-generated clips with studio-grade xfade transitions.
 */

import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import type { TransitionConfig } from '../types'

const CORE_CDN = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'

// Singleton — expensive to load (~30MB WASM), reuse across merges.
// Callbacks are stored in mutable refs so listeners are registered once
// and always delegate to the latest callbacks without needing off().
let _ffmpeg: FFmpeg | null = null
let _loaded = false
let _currentLog: (msg: string) => void = () => {}
let _currentProgress: (pct: number) => void = () => {}

async function getFFmpeg(
  onLog: (msg: string) => void,
  onProgress: (pct: number) => void,
): Promise<FFmpeg> {
  // Update callback refs — existing listeners will pick these up
  _currentLog = onLog
  _currentProgress = onProgress

  if (_ffmpeg && _loaded) return _ffmpeg

  const f = new FFmpeg()
  f.on('log',      ({ message })  => _currentLog(message))
  f.on('progress', ({ progress }) => _currentProgress(Math.min(99, Math.round(progress * 100))))

  onLog('Loading video processing engine…')
  await f.load({
    coreURL: await toBlobURL(`${CORE_CDN}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${CORE_CDN}/ffmpeg-core.wasm`, 'application/wasm'),
  })

  _ffmpeg = f
  _loaded = true
  return f
}

/** Detect actual clip duration via HTMLVideoElement */
function getClipDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(blob)
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(isFinite(video.duration) && video.duration > 0 ? video.duration : 8)
    }
    video.onerror = () => { URL.revokeObjectURL(url); resolve(8) }
    video.preload = 'metadata'
    video.src = url
  })
}

/**
 * Build an ffmpeg xfade filter_complex string.
 *
 * xfade offset formula: for transition i between clips i and i+1,
 *   offset_i = Σ(clip_duration_j, j=0..i) − Σ(transition_duration_j, j=0..i)
 *
 * Each xfade step can have a different transition type, giving per-boundary control.
 */
function buildFilterComplex(
  numInputs: number,
  clipDurations: number[],
  transitions: TransitionConfig[],
): string {
  const parts: string[] = []
  let prevLabel = '[0:v]'
  let cumulativeDuration = clipDurations[0] ?? 8
  let cumulativeTransition = 0

  for (let i = 0; i < numInputs - 1; i++) {
    const t = transitions[i] ?? { type: 'dissolve' as const, duration: 0.5 }
    cumulativeTransition += t.duration
    const offset = Math.max(0.001, cumulativeDuration - cumulativeTransition)
    const outLabel = i === numInputs - 2 ? '[vout]' : `[v${i + 1}]`

    parts.push(
      `${prevLabel}[${i + 1}:v]xfade=transition=${t.type}:duration=${t.duration.toFixed(2)}:offset=${offset.toFixed(3)}${outLabel}`
    )

    prevLabel = outLabel
    cumulativeDuration += clipDurations[i + 1] ?? 8
  }

  return parts.join(';')
}

export interface MergeInput {
  url: string
  label: string
}

export interface MergeOptions {
  inputs: MergeInput[]
  transitions: TransitionConfig[]  // length === inputs.length − 1
  onLog: (msg: string) => void
  onProgress: (pct: number) => void
}

/**
 * Merge video clips with xfade transitions.
 * Returns the merged MP4 as a Uint8Array ready for upload.
 */
export async function mergeVideos(opts: MergeOptions): Promise<Uint8Array> {
  const { inputs, transitions, onLog, onProgress } = opts

  if (inputs.length === 0) throw new Error('No clips provided')

  // Single clip — just fetch and return as-is
  if (inputs.length === 1) {
    onLog(`Fetching ${inputs[0].label}…`)
    const res = await fetch(inputs[0].url)
    if (!res.ok) throw new Error(`Fetch failed for ${inputs[0].label}: HTTP ${res.status}`)
    const buf = await res.arrayBuffer()
    onProgress(100)
    return new Uint8Array(buf)
  }

  const ffmpeg = await getFFmpeg(onLog, onProgress)

  // Fetch all clips as Blobs
  const blobs: Blob[] = []
  for (let i = 0; i < inputs.length; i++) {
    onLog(`Fetching clip ${i + 1} of ${inputs.length}: ${inputs[i].label}…`)
    const res = await fetch(inputs[i].url)
    if (!res.ok) throw new Error(`Fetch failed for ${inputs[i].label}: HTTP ${res.status}`)
    blobs.push(await res.blob())
  }

  // Detect actual clip durations for accurate xfade offsets
  onLog('Detecting clip durations…')
  const clipDurations = await Promise.all(blobs.map((b) => getClipDuration(b)))
  onLog(`Durations: ${clipDurations.map((d) => d.toFixed(2) + 's').join(', ')}`)

  // Write clips into ffmpeg virtual filesystem
  for (let i = 0; i < blobs.length; i++) {
    onLog(`Preparing clip ${i + 1}…`)
    await ffmpeg.writeFile(`in${i}.mp4`, await fetchFile(blobs[i]))
  }

  const inputArgs = blobs.flatMap((_, i) => ['-i', `in${i}.mp4`])
  const filterComplex = buildFilterComplex(blobs.length, clipDurations, transitions)

  onLog('Encoding with transitions…')
  await ffmpeg.exec([
    ...inputArgs,
    '-filter_complex', filterComplex,
    '-map', '[vout]',
    '-c:v', 'libx264',
    '-preset', 'fast',
    '-crf', '18',          // Visually lossless quality
    '-pix_fmt', 'yuv420p', // Maximum compatibility
    '-movflags', '+faststart',
    '-an',                 // Veo clips are silent; skip audio
    'out.mp4',
  ])

  onProgress(100)
  onLog('Finalizing output…')

  const data = await ffmpeg.readFile('out.mp4') as Uint8Array

  // Clean up virtual FS for next run
  for (let i = 0; i < blobs.length; i++) {
    await ffmpeg.deleteFile(`in${i}.mp4`).catch(() => {})
  }
  await ffmpeg.deleteFile('out.mp4').catch(() => {})

  return data
}
